import React, { useContext, useRef, useState } from "react";
import {
  Loader,
  TextButton,
  ModalContext,
  ModalButtonsContainer,
  useIcon,
} from "@SeemplicityDev/shared";
import type { ScopeNode } from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import useLogger from "../../../../Logger/useLogger";
import DeleteScopeGroupConfirm from "./DeleteScopeGroupConfirm";
import DeleteScopeGroupCollision from "./DeleteScopeGroupCollision";
import ScopeGuardrailModal from "./ScopeGuardrailModal";
import { useScopeDependencies } from "./useScopeDependencies";
import { useDeleteScopeGroup } from "./useScopeGuardrailMutations";
import type { NameCollision } from "./types";

export interface DeleteScopeGroupModalProps {
  groupId: string;
  groupName?: string;
  root?: ScopeNode | null;
  onDeleted: () => void;
}

// confirm → Continue → checking → either the final delete confirmation (nothing
// blocks or warns) or a review, as in the design.
type Phase = "confirm" | "checking" | "deleteConfirm" | "review";

// The BE refused but the re-checked dependencies show nothing to point at.
const REFUSED_MESSAGE =
  "The group could not be deleted. Refresh the data and try again.";

const DeleteScopeGroupModal: React.FC<DeleteScopeGroupModalProps> = ({
  groupId,
  groupName,
  root,
  onDeleted,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { getSvg } = useIcon();
  const { logWarning } = useLogger();
  const { check, loading, called, dependencies, error } =
    useScopeDependencies();
  const { deleteScopeGroup, loading: deleting } = useDeleteScopeGroup();

  const [phase, setPhase] = useState<Phase>("confirm");
  const [deleteDescendants, setDeleteDescendants] = useState(false);
  const [collisions, setCollisions] = useState<NameCollision[] | null>(null);
  const [refused, setRefused] = useState(false);

  const runCheck = () =>
    check({ id: groupId, nodeType: "group", includeDescendants: true });

  const blockedByDirect = (dependencies?.direct?.total_count ?? 0) > 0;
  const blockedByDescendants =
    deleteDescendants && (dependencies?.descendants_in_use?.length ?? 0) > 0;
  const isBlocked = blockedByDirect || blockedByDescendants;
  const hasAncestorWarning =
    !!dependencies &&
    deleteDescendants &&
    dependencies.ancestors_have_dependencies;
  const refusedWithoutReason = refused && !isBlocked && !hasAncestorWarning;

  // Synchronous guard: `deleting` only flips on the next render, so a rapid
  // double-click on Delete would otherwise fire the mutation twice.
  const inFlight = useRef(false);

  const doDelete = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    deleteScopeGroup(groupId, { deleteDescendants })
      .then(({ ok, nameCollisions }) => {
        if (ok) {
          onDeleted();
          closeModal();
          return;
        }
        if (nameCollisions.length) {
          setCollisions(nameCollisions);
        } else {
          // Recovery, not an error path: the BE refused without collisions, so
          // re-fetch the dependencies and let the review show the current reason.
          setRefused(true);
          runCheck();
        }
        setPhase("review");
      })
      .catch(async (err: unknown) => {
        // Warning, not a Sentry error: the BE logs the refusal; this is the FE trace in DD.
        logWarning(err, "Scope guardrails: delete_scope_group_v3 failed");
        // A dependency block arrives as a rejection. Recovery re-fetch: the
        // review shows what blocks the delete, or the refused message when the
        // re-check finds nothing; the generic dialog would only duplicate it.
        setRefused(true);
        setPhase("review");
        await runCheck();
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  // Continue runs the check; a block or warning stops for review, otherwise
  // the final confirmation follows. A failed check leaves the error state.
  const handleContinue = async () => {
    setPhase("checking");
    const result = await runCheck();
    if (result.status !== "ok") {
      return;
    }
    const checked = result.dependencies;
    const needsReview =
      (checked.direct?.total_count ?? 0) > 0 ||
      (deleteDescendants &&
        ((checked.descendants_in_use?.length ?? 0) > 0 ||
          checked.ancestors_have_dependencies));
    if (needsReview) {
      setPhase("review");
      return;
    }
    setPhase("deleteConfirm");
  };

  // Render ladder, first match wins. Transitions:
  //   confirm --Continue--> checking --ok, nothing blocks--> deleteConfirm --Delete--> done
  //                                  --ok, blocked/warned--> review        --Delete--> done
  //                                  --check failed-------> error (Retry re-checks, Close exits)
  //   review/deleteConfirm --refused--> review (collisions list, or re-checked reason)
  //   review/collisions --Cancel--> closed (no way back to the confirm step)
  // The two phase-owned screens (confirm, deleteConfirm) come first so a stale
  // dependency state from an earlier check can never paint over them; the
  // remaining screens all derive from the dependency check.
  if (phase === "confirm") {
    return (
      <DeleteScopeGroupConfirm
        groupName={groupName}
        deleteDescendants={deleteDescendants}
        onToggleDeleteDescendants={setDeleteDescendants}
        confirmLabel={"Continue"}
        onConfirm={handleContinue}
        onCancel={closeModal}
      />
    );
  }

  if (phase === "deleteConfirm") {
    return (
      <div className={"modal-text scope-guardrail-modal-placeholder"}>
        <div>This scope group is not connected to any items.</div>
        <div className={"margin-top-20"}>
          {"Are you sure you want to delete "}
          <span className={"modal-live-text"}>{groupName}</span>
          {" scope group?"}
        </div>
        <div>
          {deleteDescendants
            ? "This will permanently remove the scope group along with its sub-groups and scopes."
            : "This will permanently remove the scope group, and move any child scopes to the top level."}
        </div>
        <ModalButtonsContainer centerButtons>
          <TextButton
            buttonStyle={"secondary"}
            label={"Cancel"}
            onClick={closeModal}
            disabled={deleting}
            data-testid={"cancel-delete-scope-group-final"}
          />
          <TextButton
            buttonStyle={"danger"}
            label={"Delete"}
            onClick={doDelete}
            disabled={deleting}
            data-testid={"confirm-delete-scope-group-final"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  if (!loading && (error || (called && !dependencies))) {
    return (
      <div className={"modal-text scope-guardrail-modal-placeholder"}>
        <Loader
          center
          title={"Couldn't check this group's connections."}
          subtitle={"Please try again."}
        />
        <ModalButtonsContainer centerButtons>
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-delete-scope-group"}
          />
          <TextButton
            buttonStyle={"primary"}
            label={"Retry"}
            onClick={runCheck}
            data-testid={"retry-scope-group-dependencies"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  if (phase === "checking" || loading || !dependencies) {
    return (
      <div className={"modal-text scope-guardrail-modal-placeholder"}>
        <Loader
          center
          img={getSvg("icon-spinner")}
          fullHeight
          iconSize={"xxxxl"}
          title={"Checking connections..."}
          subtitle={
            "Please wait while we verify if this group is linked to any items."
          }
        />
      </div>
    );
  }

  if (collisions) {
    return (
      <DeleteScopeGroupCollision
        groupName={groupName}
        collisions={collisions}
        onCancel={closeModal}
      />
    );
  }

  const hierarchy = blockedByDirect
    ? undefined
    : {
        tree: root ?? [],
        targetId: groupId,
        descendantsInUse: dependencies.descendants_in_use,
      };
  return (
    <ScopeGuardrailModal
      entityName={groupName}
      variant={"delete"}
      outcome={isBlocked || refusedWithoutReason ? "blocked" : "warning"}
      dependencies={blockedByDirect ? dependencies.direct : undefined}
      // A direct block is explained by the dependencies table itself.
      blockedMessage={refusedWithoutReason ? REFUSED_MESSAGE : undefined}
      hierarchy={hierarchy}
      ancestorsHaveDependencies={hasAncestorWarning}
      confirmDisabled={deleting}
      onConfirm={doDelete}
      onCancel={closeModal}
      onRefresh={runCheck}
    />
  );
};

export default DeleteScopeGroupModal;
